import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import cfosService from '../../services/cfosService';
import FileTypeIcon from './FileTypeIcon';
import {
  collectDroppedFiles,
  collectInputFiles,
  uniqueRelativeDirs,
  type DroppedFileItem,
} from '../../lib/cfos/droppedFiles';
import {
  buildFolderTree,
  breadcrumbSegments,
  createVirtualFolder,
  foldersAtPath,
  joinVirtualPathTree,
  normalizeVirtualPath,
  resolveUploadDestination,
  type FolderNode,
} from '../../lib/cfos/virtualFolders';
import type { DuplicateResult, FileRecord, LinkedResult, UploadBatchProgress, UploadResult } from '../../lib/cfos/types';

export interface UploadTabProps {
  notify: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
  panelBorder: string;
  sandboxId: string;
  initialPath?: string;
}

function formatSize(value: number) {
  return value < 1024 * 1024
    ? `${Math.max(1, Math.round(value / 1024))} KB`
    : `${(value / 1024 / 1024).toFixed(1)} MB`;
}

function duplicateLocationLabel(item: DuplicateResult): string {
  if (!item.exists_in.length) return item.reason || 'File already exists in the current path';
  const [destination, ...others] = item.exists_in;
  if (!others.length) return destination;
  return `${destination} · Also in: ${others.join(', ')}`;
}

function FolderPicker({
  tree,
  path,
  onPath,
}: {
  tree: FolderNode[];
  path: string;
  onPath: (path: string) => void;
}) {
  const folders = foldersAtPath(tree, path);
  const segments = breadcrumbSegments(path);
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, maxHeight: 200, overflow: 'auto' }}>
      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mb: 1 }}>
        <Chip size="small" label="Root" onClick={() => onPath('/')} color={path === '/' ? 'primary' : 'default'} />
        {segments.map((seg) => (
          <Chip key={seg.path} size="small" label={seg.name} onClick={() => onPath(seg.path)} />
        ))}
      </Stack>
      {folders.map((folder) => (
        <Button key={folder.path} size="small" onClick={() => onPath(folder.path)} sx={{ display: 'block', textAlign: 'left' }}>
          {folder.name}
        </Button>
      ))}
      {!folders.length && path !== '/' && (
        <Typography variant="caption" color="text.secondary">No subfolders</Typography>
      )}
    </Box>
  );
}

const UploadTab: React.FC<UploadTabProps> = ({ notify, panelBorder, sandboxId, initialPath }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [queue, setQueue] = useState<DroppedFileItem[]>([]);
  const [workspaceFiles, setWorkspaceFiles] = useState<FileRecord[]>([]);
  const [extraFolders, setExtraFolders] = useState<string[]>([]);
  const [canCreateFolders, setCanCreateFolders] = useState(true);
  const [jobRoles, setJobRoles] = useState<{ id: number; name: string }[]>([]);
  const [newFolderRoles, setNewFolderRoles] = useState<{ id: number; name: string }[]>([]);
  const [path, setPath] = useState(() => resolveUploadDestination(initialPath));
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadBatchProgress | null>(null);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [preflight, setPreflight] = useState<Record<string, string[]>>({});
  const [newFolderName, setNewFolderName] = useState('');

  useEffect(() => {
    if (!sandboxId) return;
    cfosService.files({ sandboxId }).then(setWorkspaceFiles).catch(() => setWorkspaceFiles([]));
    cfosService.listFolders(sandboxId)
      .then((data) => {
        setExtraFolders(data.paths);
        setCanCreateFolders(data.can_create_folders);
        if (data.roles?.length) setJobRoles(data.roles);
      })
      .catch(() => {
        setExtraFolders([]);
        setCanCreateFolders(true);
      });
  }, [sandboxId]);

  useEffect(() => {
    if (initialPath) setPath(resolveUploadDestination(initialPath));
  }, [initialPath]);

  const tree = useMemo(
    () => buildFolderTree([
      ...workspaceFiles.flatMap((file) => file.paths.map((item) => item.path)),
      ...extraFolders,
      path,
    ]),
    [workspaceFiles, extraFolders, path],
  );

  const folderCount = useMemo(() => uniqueRelativeDirs(queue).length, [queue]);

  const mergeQueue = (incoming: DroppedFileItem[]) => {
    setQueue((current) => {
      const next = [...current];
      for (const item of incoming) {
        if (next.some((existing) => existing.file.name === item.file.name
          && existing.file.size === item.file.size
          && existing.relativeDir === item.relativeDir)) continue;
        next.push(item);
      }
      return next;
    });
  };

  const runPreflight = async (items: DroppedFileItem[]) => {
    if (items.length > 20) return;
    const checks = await Promise.all(items.map(async (item) => {
      try {
        const check = await cfosService.checkFilename(item.file.name);
        return [item.file.name, check.matches.flatMap((match) => match.paths.map((location) => location.path))] as const;
      } catch {
        return [item.file.name, []] as const;
      }
    }));
    setPreflight((current) => ({ ...current, ...Object.fromEntries(checks) }));
  };

  const addTree = async (treeData: { items: DroppedFileItem[]; emptyRelativeDirs: string[] }) => {
    if (!treeData.items.length && !treeData.emptyRelativeDirs.length) {
      notify('No files found to upload.', 'warning');
      return;
    }
    if (treeData.emptyRelativeDirs.length) {
      const destination = normalizeVirtualPath(path);
      const emptyPaths = treeData.emptyRelativeDirs
        .map((relative) => joinVirtualPathTree(destination, relative))
        .filter((value): value is string => Boolean(value) && value !== '/');
      if (emptyPaths.length) {
        if (!canCreateFolders) {
          notify('You are not allowed to create folders here.', 'error');
        } else {
          try {
            const stored = await cfosService.createFolders(sandboxId, emptyPaths);
            setExtraFolders(stored);
          } catch {
            /* ignore */
          }
        }
      }
    }
    if (!treeData.items.length) return;
    mergeQueue(treeData.items);
    await runPreflight(treeData.items);
  };

  const upload = async () => {
    const destination = normalizeVirtualPath(path);
    if (!queue.length || !sandboxId) return;
    const items = [];
    for (const item of queue) {
      const dest = item.relativeDir
        ? joinVirtualPathTree(destination, item.relativeDir)
        : destination;
      if (!dest) {
        notify(`Skipped ${item.file.name}: invalid destination path.`, 'error');
        continue;
      }
      items.push({ file: item.file, path: dest });
    }
    if (!items.length) return;

    setLoading(true);
    setResult(null);
    setUploadProgress({ completed: 0, total: items.length, batchIndex: 0, batchCount: 0 });
    try {
      const output = await cfosService.uploadInBatches(items, sandboxId, {
        onProgress: setUploadProgress,
      });
      setResult(output);
      setQueue([]);
      setPreflight({});
      const total = output.uploaded.length + output.linked.length;
      notify(total ? `${total} file(s) processed.` : 'Upload finished with warnings.', total ? 'success' : 'warning');
      cfosService.files({ sandboxId }).then(setWorkspaceFiles).catch(() => undefined);
      cfosService.listFolders(sandboxId)
        .then((data) => {
          setExtraFolders(data.paths);
          setCanCreateFolders(data.can_create_folders);
          if (data.roles?.length) setJobRoles(data.roles);
        })
        .catch(() => undefined);
    } catch (error) {
      notify((error as Error).message, 'error');
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const createFolder = async () => {
    if (!canCreateFolders) {
      notify('You are not allowed to create folders here.', 'error');
      return;
    }
    const folderResult = createVirtualFolder(path, newFolderName, [
      ...workspaceFiles.flatMap((file) => file.paths.map((item) => item.path)),
      ...extraFolders,
      path,
    ]);
    if ('error' in folderResult) {
      notify(folderResult.error, 'error');
      return;
    }
    try {
      const stored = await cfosService.createFolders(
        sandboxId,
        [folderResult.path],
        newFolderRoles.map((role) => role.id),
      );
      setExtraFolders(stored);
      setPath(folderResult.path);
      setNewFolderName('');
      setNewFolderRoles([]);
      notify(`Folder ${folderResult.path} created.`, 'success');
    } catch (error) {
      notify((error as Error).message, 'error');
    }
  };

  const sample = queue.slice(0, 40);
  const linked: LinkedResult[] = result?.linked || [];
  const duplicates: DuplicateResult[] = result?.duplicates || [];
  const errorRows = (result?.errors || []).slice(0, 50);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void collectDroppedFiles(e.dataTransfer).then((treeData) => addTree(treeData));
        }}
        sx={{
          border: 2,
          borderStyle: 'dashed',
          borderColor: dragging ? '#ccba9b' : panelBorder,
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          bgcolor: dragging ? 'action.hover' : 'background.paper',
        }}
      >
        <UploadIcon sx={{ fontSize: 40, color: '#ccba9b', mb: 1 }} />
        <Typography variant="h6" fontWeight={700}>Drop files or folders here</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Folder structure is preserved under the destination path
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
          <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>Choose files</Button>
          <Button variant="outlined" onClick={() => folderInputRef.current?.click()}>Choose folder</Button>
        </Stack>
        <input ref={fileInputRef} type="file" multiple hidden onChange={(e) => { void addTree(collectInputFiles(e.target.files)); e.target.value = ''; }} />
        <input
          ref={folderInputRef}
          type="file"
          multiple
          hidden
          // @ts-expect-error webkitdirectory
          webkitdirectory=""
          directory=""
          onChange={(e) => { void addTree(collectInputFiles(e.target.files)); e.target.value = ''; }}
        />
      </Box>

      {queue.length > 0 && (
        <Box sx={{ mt: 2, border: 1, borderColor: panelBorder, borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderBottom: 1, borderColor: panelBorder }}>
            <Typography variant="subtitle2">Ready to upload ({queue.length})</Typography>
            <Button size="small" color="error" onClick={() => { setQueue([]); setPreflight({}); }}>Clear all</Button>
          </Box>
          {sample.map((item) => (
            <Stack key={`${item.relativeDir}/${item.file.name}-${item.file.size}`} direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <FileTypeIcon filename={item.file.name} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>{item.file.name}</Typography>
                {item.relativeDir && <Typography variant="caption" color="text.secondary" noWrap>{item.relativeDir}</Typography>}
              </Box>
              {preflight[item.file.name]?.length > 0 && <Chip size="small" label="Exists" color="warning" />}
              <Typography variant="caption" color="text.secondary">{formatSize(item.file.size)}</Typography>
              <IconButton size="small" onClick={() => setQueue((items) => items.filter((entry) => entry !== item))}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          {queue.length > sample.length && (
            <Typography variant="caption" sx={{ p: 1, display: 'block' }}>Showing {sample.length} of {queue.length}</Typography>
          )}
        </Box>
      )}

      <Stack spacing={2} sx={{ mt: 2, p: 2, border: 1, borderColor: panelBorder, borderRadius: 1 }}>
        <Typography variant="subtitle2">Destination</Typography>
        <FolderPicker tree={tree} path={path} onPath={(next) => setPath(normalizeVirtualPath(next))} />
        {canCreateFolders && (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <TextField size="small" placeholder="New folder name" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} sx={{ flex: 1 }} />
              <Button size="small" variant="outlined" disabled={!newFolderName.trim()} onClick={createFolder}>Create</Button>
            </Stack>
            <Autocomplete
              multiple
              size="small"
              options={jobRoles}
              getOptionLabel={(option) => option.name}
              value={newFolderRoles}
              onChange={(_, value) => setNewFolderRoles(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Visible to"
                  placeholder={newFolderRoles.length ? undefined : 'All roles'}
                />
              )}
            />
          </Stack>
        )}
        {folderCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            {folderCount} relative folder{folderCount === 1 ? '' : 's'} under {path === '/' ? 'Root' : path}
          </Typography>
        )}
        <Box>
          <Button variant="contained" disabled={!queue.length || loading} onClick={() => void upload()}>
            {uploadProgress ? `Uploading ${uploadProgress.completed} / ${uploadProgress.total}` : `Upload ${queue.length || ''} file${queue.length === 1 ? '' : 's'}`}
          </Button>
        </Box>
        {loading && uploadProgress && (
          <LinearProgress variant="determinate" value={(uploadProgress.completed / uploadProgress.total) * 100} />
        )}
      </Stack>

      {linked.length > 0 && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Linked to existing content</Typography>
          {linked.map((item) => (
            <Typography key={item.filename} variant="body2">{item.filename} → {item.other_locations.join(', ') || item.reason}</Typography>
          ))}
        </Alert>
      )}

      {duplicates.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Already in folder</Typography>
          {duplicates.map((item) => (
            <Typography key={item.filename} variant="body2">{item.filename}: {duplicateLocationLabel(item)}</Typography>
          ))}
        </Alert>
      )}

      {errorRows.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorRows.map((item) => (
            <Typography key={`${item.filename}-${item.message}`} variant="body2">
              <strong>{item.filename}:</strong> {item.message}
            </Typography>
          ))}
        </Alert>
      )}
    </Box>
  );
};

export default UploadTab;
